import { graphql } from "gatsby";
import { useUiBkgVideoWorkaround, EventAggregator, PrimaryButtonLink } from "@parksandresorts/core";
import UiPicture from "@rnb/ui-picture";
import UiBkgVideo from "@rnb/ui-bkg-video";
import styles from "./hero-block.module.less";
import BrowserSupportHelper from "components/helpers/browser-support-helper";
import Parallax from "components/block-elements/parallax/parallax";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import { getHeroPicture } from "components/helpers/picture-helper";

const breakPointSmallDevice = 576;

export default function HeroBlock(props: Queries.HeroBlockFragment) {
  const { textPositioning, entryTitle, componentTheme, enableTextBackground, heroParagraph, buttonLabel } = props;

  const imageUrlForSmallDevice = props.heroImageSmallDevice?.file?.url;
  const imageUrlForLargeDevice = props.heroImageLargeDevice?.file?.url;
  const buttonLinkInternalLink = props.buttonLink?.internalLink?.slug;
  const buttonLinkExternalLink = props.buttonLink?.externalUrl;
  const videoUrl = props.backgroundVideo?.file?.url;
  const videoUrlMobile = props.backgroundVideoMobile?.file?.url;

  const isInternetExplorerOrEdge = BrowserSupportHelper.checkIsBrowserEdge() || BrowserSupportHelper.checkIsBrowserIE();

  const buttonLink = buttonLinkExternalLink || buttonLinkInternalLink || "";

  const smallDevice = props.heroImageSmallDevice;
  const largeDevice = props.heroImageLargeDevice;

  const videoKey = useUiBkgVideoWorkaround(breakPointSmallDevice);

  const content = (
    <>
      <TextAndDecoration
        textPositioning={textPositioning}
        entryTitle={entryTitle}
        enableTextBackground={enableTextBackground}
        heroParagraph={heroParagraph}
        buttonLabel={buttonLabel}
        buttonLink={buttonLink}
      />
      <div className={styles.hero__gradientLayer} />
    </>
  );

  return (
    <section
      className={["block block--no-margin", componentTheme ? `component-theme-${componentTheme}` : ""].join(" ")}
      data-contentful-id={props.contentful_id}
    >
      {videoUrl ? (
        <Parallax parallaxOffset={64}>
          <UiBkgVideo
            key={videoKey}
            wrapperExtraClasses={styles.hero__videoWrapper}
            videoExtraClasses={[
              styles.hero__video,
              "data-block-in-nav-parallax",
              isInternetExplorerOrEdge ? styles.hero__video_ie : styles.hero__video_normal,
            ].join(" ")}
            srcDesktop={videoUrl}
            srcMobile={videoUrlMobile || undefined}
            autoplay={true}
            muted={true}
            preload="auto"
            playsinline={true}
            loop={true}
            breakPointSmallDevice={breakPointSmallDevice}
            posterSmallDevice={imageUrlForSmallDevice ? imageUrlForSmallDevice + "?q=30&fm=webp" : undefined}
            posterLargeDevice={imageUrlForLargeDevice ? imageUrlForLargeDevice + "?q=30&fm=webp" : undefined}
            videoPosterExtraClass={styles.hero__videoPoster}
            videoPosterHiddenClass={styles.hero__videoPosterHidden}
          >
            {content}
          </UiBkgVideo>
        </Parallax>
      ) : (
        <Parallax parallaxOffset={64}>
          <UiPicture
            {...getHeroPicture(smallDevice, largeDevice, `${props.contentful_id}_hero`)}
            useLowQualityImage={true}
            classNamePictureWrapper={styles.heroPictureWrapper}
          />
          {content}
        </Parallax>
      )}
    </section>
  );
}

function TextAndDecoration(
  props: Pick<
    Queries.HeroBlockFragment,
    "textPositioning" | "entryTitle" | "enableTextBackground" | "heroParagraph" | "buttonLabel"
  > & { buttonLink: string },
) {
  const { textPositioning, entryTitle, enableTextBackground, heroParagraph, buttonLabel, buttonLink } = props;

  const linkWrapperUrl = buttonLink && !buttonLabel ? buttonLink : null;

  return (
    <LinkWrapper url={linkWrapperUrl}>
      <div className={styles.hero__infoOuterwrapper}>
        <div className={[styles.hero__infoInnerwrapper, styles[textPositioning ?? ""]].join(" ")}>
          {entryTitle && (
            <h1
              className={[
                styles.hero__header,
                enableTextBackground ? styles.hero__colorText : styles.hero__textShadow,
                "hero-heading",
              ].join(" ")}
            >
              {entryTitle}
            </h1>
          )}
          {heroParagraph && (
            <p className={[styles.hero__paragraph, enableTextBackground ? "" : styles.hero__textShadow].join(" ")}>
              <span className={enableTextBackground ? styles.hero__paragraphBackground : ""}>{heroParagraph}</span>
            </p>
          )}
          {buttonLabel && buttonLink && (
            <span
              className={styles.buttonWrapper}
              onClick={() => {
                EventAggregator.publish("trackEvent", {
                  category: "CTA Click",
                  action: buttonLabel,
                  label: buttonLink,
                });
              }}
            >
              <PrimaryButtonLink label={buttonLabel} className={styles.ctaButton} url={buttonLink} />
            </span>
          )}
        </div>
      </div>
    </LinkWrapper>
  );
}

export const fragmentHeroBlock = graphql`
  fragment HeroBlock on ContentfulHeroBlock {
    ... on ContentfulHeroBlock {
      contentful_id
      id
      entryTitle
      heroParagraph
      buttonLabel
      textPositioning
      enableTextBackground
      componentTheme
      buttonLink {
        internalLink {
          slug
        }
        externalUrl
      }
      heroImageSmallDevice {
        file {
          url
        }
      }
      heroImageLargeDevice {
        description
        file {
          url
        }
      }
      backgroundVideo {
        file {
          url
        }
      }
      backgroundVideoMobile {
        file {
          url
        }
      }
    }
  }
`;
